import React, { useState, useEffect } from 'react';
import { Leaf } from 'lucide-react';
import PropertyCard from './ui/PropertyCard';
import { normalize } from '../map/LayerAliasUtils';
import Alert from '@mui/material/Alert';

/**
 * Componente para visualização consolidada de dados do Cadastro Ambiental Rural (CAR)
 * Exibe o somatório das áreas de todas as propriedades CAR disponíveis como um dashboard
 */
const CarDashboardTab = ({ propertyData }) => {
  // Estado para armazenar os dados consolidados
  const [consolidatedData, setConsolidatedData] = useState(null);

  // Helper function para extrair e somar campos numéricos de objetos aninhados
  const getField = (obj, ...possibleKeys) => {
    if (!obj) return '';

    for (const key of possibleKeys) {
      // Verificar no objeto raiz
      if (obj[key] != null) return obj[key];

      // Verificar no objeto details (padrão comum no sistema)
      if (obj.details && obj.details[key] != null) return obj.details[key];

      // Verificar no atributos (padrão GeoJSON)
      if (obj.attributes && obj.attributes[key] != null) return obj.attributes[key];

      // Nível properties (GeoJSON)
      if (obj.properties && obj.properties[key] != null) return obj.properties[key];

      // Verificar versão normalizada (sem acentos)
      const normalizedKey = normalize(key);
      for (const objKey of Object.keys(obj)) {
        if (normalize(objKey) === normalizedKey) return obj[objKey];
      }
    }

    return '';
  };

  /**
   * Converte string ou número para float com tratamento adequado
   * Força conversão de strings para números com tratamento de casos especiais
   */
  const getNumber = (obj, ...possibleKeys) => {
    const raw = getField(obj, ...possibleKeys);
    if (raw === '' || raw == null) return 0;

    // Tratar string 'nan' e valor numérico NaN
    if (
      (typeof raw === 'string' && raw.toLowerCase() === 'nan') ||
      (typeof raw === 'number' && isNaN(raw))
    ) {
      return 0;
    }

    // Garantir conversão de strings para números
    // Tratar formatos com vírgula como separador decimal (padrão brasileiro)
    if (typeof raw === 'string') {
      // Remover não-dígitos, exceto pontos e vírgulas
      const cleaned = raw.replace(/[^0-9.,]/g, '');
      // Substituir vírgula por ponto e converter
      const normalized = cleaned.replace(/,/g, '.');
      const n = parseFloat(normalized);
      return !isNaN(n) ? n : 0;
    }

    // Se já for número
    return typeof raw === 'number' ? raw : 0;
  };

  // Dicionário com as áreas do CAR
  const areaComposicao = [
    {
      key: 'area_de_pousio',
      accentKey: 'área_de_pousio',
      label: 'Pousio',
      color: 'bg-amber-400',
      alias: 'ÁREA DE POUSIO',
    },
    {
      key: 'area_de_preservacao_permanente',
      accentKey: 'área_de_preservação_permanente',
      label: 'Preservação Permanente',
      color: 'bg-blue-400',
      alias: 'APP',
    },
    {
      key: 'area_de_reserva_legal_aprovada_e_nao_averbada',
      accentKey: 'área_de_reserva_legal_aprovada_e_não_averbada',
      label: 'Reserva Legal Aprovada Não Averbada',
      color: 'bg-green-400',
      alias: 'RESERVA LEGAL',
    },
    {
      key: 'area_de_reserva_legal_averbada',
      accentKey: 'área_de_reserva_legal_averbada',
      label: 'Reserva Legal Averbada',
      color: 'bg-emerald-400',
      alias: 'RESERVA LEGAL',
    },
    {
      key: 'area_de_reserva_legal_proposta',
      accentKey: 'área_de_reserva_legal_proposta',
      label: 'Reserva Legal Proposta',
      color: 'bg-lime-400',
      alias: 'RESERVA LEGAL',
    },
    {
      key: 'area_de_servidao_administrativa',
      accentKey: 'área_de_servidão_administrativa',
      label: 'Servidão Administrativa',
      color: 'bg-violet-400',
      alias: 'ÁREA DE SERVIDÃO',
    },
    {
      key: 'area_de_uso_restrito',
      accentKey: 'área_de_uso_restrito',
      label: 'Uso Restrito',
      color: 'bg-rose-400',
      alias: 'USO RESTRITO',
    },
    {
      key: 'area_liquida',
      accentKey: 'área_líquida',
      label: 'Área Líquida',
      color: 'bg-cyan-400',
      alias: '',
    },
    {
      key: 'area_nao_classificada',
      accentKey: 'área_não_classificada',
      label: 'Não Classificada',
      color: 'bg-gray-400',
      alias: '',
    },
    {
      key: 'area_remanescente_de_vegetacao_nativa',
      accentKey: 'área_remanescente_de_vegetação_nativa',
      label: 'Vegetação Nativa',
      color: 'bg-teal-400',
      alias: 'VEGETAÇÃO NATIVA',
    },
    {
      key: 'area_rural_consolidada',
      accentKey: 'área_rural_consolidada',
      label: 'Rural Consolidada',
      color: 'bg-yellow-400',
      alias: 'ÁREA CONSOLIDADA',
    },
  ];

  // Consolidar os dados de todos os registros CAR
  useEffect(() => {
    if (!propertyData || !propertyData.properties || !propertyData.properties.car) {
      setConsolidatedData(null);
      return;
    }

    // Normalizar para array, conforme padrão de databinding do projeto
    const carData = Array.isArray(propertyData.properties.car)
      ? propertyData.properties.car
      : [propertyData.properties.car];

    // Objeto para armazenar as áreas consolidadas
    const consolidatedAreas = {};

    // Total de registros processados
    const totalRegistros = carData.length;

    // Iterar por todos os registros CAR
    carData.forEach((carRecord) => {
      // Para cada tipo de área, somar ao consolidado
      areaComposicao.forEach((areaType) => {
        const value = getNumber(
          carRecord,
          areaType.key,
          areaType.accentKey,
          `details.${areaType.key}`,
          `details.${areaType.accentKey}`,
        );

        if (!consolidatedAreas[areaType.key]) {
          consolidatedAreas[areaType.key] = 0;
        }

        consolidatedAreas[areaType.key] += value;
      });
    });

    // Calcular área total consolidada somando area_da_propriedade de todos os registros
    let consolidatedTotalArea = 0;

    // Iterar por todos os registros CAR novamente para somar apenas area_da_propriedade
    carData.forEach((carRecord) => {
      const areaValue = getNumber(
        carRecord,
        'area_da_propriedade',
        'área_da_propriedade',
        'area_total',
        'área_total',
        'details.area_da_propriedade',
        'details.área_da_propriedade',
        'details.area_total',
        'details.área_total',
      );

      consolidatedTotalArea += areaValue;
    });

    // Se, por algum motivo, a área total ainda estiver zerada, usar soma de outras áreas como fallback
    if (consolidatedTotalArea < 1) {
      consolidatedTotalArea = Object.values(consolidatedAreas).reduce((sum, val) => sum + val, 0);
    }

    // Criar objeto de dados consolidados
    const composicaoData = areaComposicao.map((item) => {
      const valor = consolidatedAreas[item.key] || 0;
      const percent = consolidatedTotalArea > 0 ? (valor / consolidatedTotalArea) * 100 : 0;

      return {
        ...item,
        valor,
        percent,
        valorFormatado:
          valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
          ' ha',
      };
    });

    // Atualizar o estado com dados consolidados
    setConsolidatedData({
      totalRegistros,
      areaTotalHa: consolidatedTotalArea,
      composicaoData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData]);

  // Função para dividir items em colunas para layout
  function _splitInColumns(arr, numCols) {
    // Ajusta o número de colunas se houver poucos itens
    const effectiveCols = arr.length < numCols ? arr.length : numCols;

    // Se não houver itens, retorna array vazio
    if (arr.length === 0) return [];

    const cols = Array.from({ length: effectiveCols }, () => []);
    arr.forEach((item, idx) => {
      cols[idx % effectiveCols].push(item);
    });
    return cols;
  }

  // Se não houver dados consolidados, mostre mensagem
  if (!consolidatedData) {
    return (
      <div className="space-y-4">
        <div className="flex items-center">
          <Leaf className="text-green-600 mr-2" size={20} />
          <h3 className="text-lg font-medium text-green-800">Dashboard CAR</h3>
        </div>
        <Alert severity="info">Nenhum dado CAR disponível para consolidação.</Alert>
      </div>
    );
  }

  // Agrupar itens por categoria para exibição
  const areaPropriedadeItems = consolidatedData.composicaoData.filter((item) =>
    [
      'area_rural_consolidada',
      'area_de_servidao_administrativa',
      'area_nao_classificada',
      'area_de_pousio',
      'area_liquida',
    ].includes(item.key),
  );

  const ambientalItems = consolidatedData.composicaoData.filter((item) =>
    [
      'area_de_reserva_legal_proposta',
      'area_de_reserva_legal_aprovada_e_nao_averbada',
      'area_de_reserva_legal_averbada',
      'area_remanescente_de_vegetacao_nativa',
      'area_de_uso_restrito',
    ].includes(item.key),
  );

  const appItems = consolidatedData.composicaoData.filter((item) =>
    ['area_de_preservacao_permanente'].includes(item.key),
  );

  // Renderizar conteúdo consolidado
  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Leaf className="text-green-600 mr-2" size={20} />
          <h3 className="text-lg font-medium text-green-800">Dashboard CAR</h3>
        </div>
        <div className="text-sm font-medium text-gray-500">
          {consolidatedData.totalRegistros}{' '}
          {consolidatedData.totalRegistros === 1 ? 'registro' : 'registros'} consolidados
        </div>
      </div>

      <PropertyCard subtitle="Dashboard CAR Consolidado" color="green">
        {/* Seção: Área da Propriedade */}
        <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
          <h4 className="font-medium text-sm text-gray-700 mb-2">
            Área da Propriedade (Consolidada)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Área Total (sem gráfico de barra pois é referência) */}
            <div className="bg-white rounded-lg px-3 py-2 flex flex-col border border-gray-100">
              <span className="text-xs text-gray-600 font-medium mb-1">Área Total</span>
              <span className="text-base font-bold text-gray-900">
                {consolidatedData.areaTotalHa.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) + ' ha'}
              </span>
            </div>

            {/* Demais áreas com gráficos de barra */}
            {areaPropriedadeItems.map((item) => (
              <div
                key={item.key}
                className="bg-white rounded-lg px-3 py-2 flex flex-col border border-gray-100"
              >
                <span className="text-xs text-gray-600 font-medium mb-1">{item.label}</span>
                <span className="text-base font-bold text-gray-900">{item.valorFormatado}</span>
                <div className="w-full h-2 rounded bg-gray-200 mt-1 mb-1">
                  <div
                    className={`h-2 rounded ${item.color}`}
                    style={{ width: `${Math.max(1, Math.min(100, item.percent))}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">{item.percent.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Seção: Ambiental */}
        <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
          <h4 className="font-medium text-sm text-gray-700 mb-2">Ambiental (Consolidado)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ambientalItems.map((item) => (
              <div
                key={item.key}
                className="bg-white rounded-lg px-3 py-2 flex flex-col border border-gray-100"
              >
                <span className="text-xs text-gray-600 font-medium mb-1">{item.label}</span>
                <span className="text-base font-bold text-gray-900">{item.valorFormatado}</span>
                <div className="w-full h-2 rounded bg-gray-200 mt-1 mb-1">
                  <div
                    className={`h-2 rounded ${item.color}`}
                    style={{ width: `${Math.max(1, Math.min(100, item.percent))}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">{item.percent.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Seção: APP */}
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
          <h4 className="font-medium text-sm text-gray-700 mb-2">APP (Consolidado)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {appItems.map((item) => (
              <div
                key={item.key}
                className="bg-white rounded-lg px-3 py-2 flex flex-col border border-gray-100"
              >
                <span className="text-xs text-gray-600 font-medium mb-1">{item.label}</span>
                <span className="text-base font-bold text-gray-900">{item.valorFormatado}</span>
                <div className="w-full h-2 rounded bg-gray-200 mt-1 mb-1">
                  <div
                    className={`h-2 rounded ${item.color}`}
                    style={{ width: `${Math.max(1, Math.min(100, item.percent))}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">{item.percent.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </PropertyCard>
    </div>
  );
};

export default CarDashboardTab;
